import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as slugify from 'slugify';
import { Theme } from '../layouts/ats-service/service-models/theme';
import { nullSafePath } from '../shared/helpers/null-safe-path';
import { PagedResultRequest } from '../shared/models/PagedResultRequest';
import { JobService } from './job-service/job.service';
import { Facet, FacetResult, JobListModel, VisibilityTypes } from './job-service/models/JobListModel';
import { JobRequest } from './job-service/models/JobRequest';
import { JobsListModel } from './job-service/models/JobsListModel';
import * as _ from 'lodash';

@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.scss']
})
export class JobsComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  @ViewChild('jobTable') jobTable: DatatableComponent;

  private fetchDataSubject = new Subject<any>();
  private readonly facetOrder = ['departmentName', 'locationCityProvince', 'brandName', 'employmentTypeName', 'experience'];
  model: JobsListModel;
  search: string;
  identifier: string;
  theme: Theme;
  basePath: string;
  expand: boolean;
  hasServerError = false;
  loading = false;
  isMobile: boolean;
  subBrandIdentifier: string;
  internalJobBoardIdentifier: string;
  facetFilters: object = {};
  facetFiltersCount = 0;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private service: JobService,
    private deviceService: DeviceDetectorService,
    private location: Location) {
      this.isMobile = this.deviceService.isMobile();
    }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    sessionStorage.removeItem('completeApplicationPartRequestId');
    this.subscription.add(
      this.route.paramMap.subscribe(p => {
        this.identifier = p.get('identifier');
        this.subBrandIdentifier = p.get('subBrandIdentifier');
        this.internalJobBoardIdentifier = p.get('jobBoardIdentifier');
      }));

    this.subscription.add(this.route.parent.data
      .subscribe((data: { model: Theme }) => {
        this.theme = Object.assign(new Theme(), data.model);
        this.theme.apply();
      })
    );

    this.subscription.add(this.route.data
      .subscribe((data: { model: JobsListModel, isInternal: boolean }) => {
        this.model = data.model;
        this.model.jobs.results = this.model.jobs.results.map(x => Object.assign(new JobListModel(), x));

        const experienceFacetName = 'experience';
        const experienceFacet = this.model.facets.find(x => x.name === experienceFacetName);
        if (experienceFacet && experienceFacet.results.length === 0) {
          this.model.facets = this.model.facets.filter(x => x.name !== experienceFacetName);
        }

        this.model.facets = _.sortBy(this.model.facets, [function (x) { return x.name; }]);
        this.model.facets.forEach(x => {
          x.results.forEach(y => y.displayValue = this.getFacetResultDisplayValue(x, y));
          x.results = _.sortBy(x.results, [function (y) { return y.displayValue; }]);
        });
        this.model.facets.forEach(x => {
          if (this.facetResultSortOrder[x.name]) {
            x.results.sort((a, b) =>
              this.facetResultSortOrder[x.name].indexOf(a.value) - this.facetResultSortOrder[x.name].indexOf(b.value));
          }
        });

        this.subscription.add(this.route.queryParams.subscribe(params => {
          const facetFilterMap = {
              'departmentName': 'departments',
              'employmentTypeName': 'employmentTypes',
              'locationCityProvince': 'locationCityProvinces',
              'brandName': 'brands',
              'experience': 'experienceLevels',
              'locationName': 'locationNames'
          };
          const request: JobRequest = Object.assign(new JobRequest(), params);
          
          request.filter = {};
          this.model.facets.forEach(x => x.results.forEach(y => this.markIsSelected(y, request[`filter.${facetFilterMap[x.name]}`])));
        }));
      }));

    this.fetchDataSubject.asObservable().pipe(
      debounceTime(200)
    ).subscribe(x => {
      this.fetchData();
    });
  }

  get isInternal(): boolean {
    return this.internalJobBoardIdentifier !== null;
  }

  clearAllFacetFilters() {
    this.model.facets.forEach(x => this.clearFacetFilters(x));
    this.updateFacetFiltersCount();
    this.fetchDataSubject.next();
  }

  clearFacetFilters(facet: Facet) {
    facet.results.forEach(x => x.isSelected = false);

    if (!this.isMobile) {
      this.fetchDataSubject.next();
    }
    
    this.updateFacetFiltersCount();
  }

  updateFacetFiltersCount() {
    this.facetFiltersCount = 0;
    this.model.facets.forEach(x => {
      x.results.forEach(y => {
        if (y.isSelected) {
          this.facetFiltersCount++;
        }
      });
    });
  }

  private facetResultDisplayNameMap = {
    'experience': {
      'EntryLevel': 'Entry-Level',
      'MidLevel': 'Mid-Level'
    }
  };

  private facetResultSortOrder = {
    'experience': [
      'EntryLevel',
      'Intermediate',
      'MidLevel',
      'Senior',
      'Executive',
      'Other'
    ]
  };

  getFacetResultDisplayValue(facet: Facet, facetResult: FacetResult): string {
    if (this.facetResultDisplayNameMap[facet.name]) {
      if (this.facetResultDisplayNameMap[facet.name][facetResult.value]) {
        return this.facetResultDisplayNameMap[facet.name][facetResult.value];
      }
    }

    return facetResult.value as unknown as string;
  }

  onFacetFilterChange() {
    if (!this.isMobile) {
      this.fetchDataSubject.next();
    }
  }

  slugify(value: string) {
    return slugify.default(value, { lower: true });
  }

  getFacetDisplayName(facet: Facet): string {
    switch (facet.name) {
      case 'departmentName': return 'Department';
      case 'employmentTypeName': return 'Employment Type';
      case 'locationCityProvince': return 'Location (by City)';
      case 'brandName': return 'Brand';
      case 'experience': return 'Experience';
      case 'locationName': return 'Location (by Name)';
      default: throw Error('Unknown Facet');
    }
  }

  getSubBrandPath(job: JobListModel): string {
    const location = this.model.locations.find(x => x.id === job.locationId);
    if (!location) { return ''; }
    const subBrand = this.model.subBrands.find(x => x.id === location.subBrandId);
    
    return !!subBrand ? `/${subBrand.identifier}` : '';
  }

  goToJobsPage() {
    this.router.navigate(nullSafePath(this.identifier, this.subBrandIdentifier));
  }

  lastItemOffset() {
    return Math.min(this.model.jobs.pageNumber * this.model.jobs.pageSize,
      this.model.jobs.totalResults);
  }

  fetchData() {
    this.updateFacetFiltersCount();
    this.processFetchedData(this.triggerDataFetch());
  }

  applyFilter(departmentId: number) {
    this.fetchData();
  }

  applyMobile() {
    this.expand = false;
    this.model.jobs.results = new Array<JobListModel>();
    setTimeout(() => {
      this.fetchData();
    }, 0);
  }

  get postedDateField() {
    return !this.isInternal ? 'PostedDate' : 'InternalPostedDate';
  }

  private processFetchedData(observable: Observable<JobsListModel>) {
    this.subscription.add(
      observable.subscribe(data => {
        this.model.jobs = data.jobs;
        this.model.jobs.results = this.model.jobs.results.map(x => Object.assign(new JobListModel(), x));
        },
        error => {
          this.hasServerError = true;
        },
        () => {
          this.loading = false;
        }
      ));
  }

  private getFacet(name: string) {
    return this.model.facets.find(x => x.name === name);
  }

  private triggerDataFetch(): Observable<JobsListModel> {
    this.loading = true;
    const request = Object.assign(new JobRequest(), PagedResultRequest.fromDatatable(this.jobTable));
    request.subBrandIdentifier = this.subBrandIdentifier;
    request.filter = {};
    const brandsFacet = this.getFacet('brandName');
    const departmentsFacet = this.getFacet('departmentName');
    const employmentTypesFacet = this.getFacet('employmentTypeName');
    const locationCityProvinceFacet = this.getFacet('locationCityProvince');
    const experienceFacet = this.getFacet('experience');
    const locationNameFacet = this.getFacet('locationName');
    request.filter.brands = brandsFacet && brandsFacet.results.some(x => x.isSelected)
      ? brandsFacet.results.filter(x => x.isSelected).map(x => x.value) : null;
    request.filter.departments = departmentsFacet && departmentsFacet.results.some(x => x.isSelected)
      ? departmentsFacet.results.filter(x => x.isSelected).map(x => x.value) : null;
    request.filter.employmentTypes = employmentTypesFacet && employmentTypesFacet.results.some(x => x.isSelected)
      ? employmentTypesFacet.results.filter(x => x.isSelected).map(x => x.value) : null;
    request.filter.locationCityProvinces = locationCityProvinceFacet && locationCityProvinceFacet.results.some(x => x.isSelected)
      ? locationCityProvinceFacet.results.filter(x => x.isSelected).map(x => x.value) : null;
    request.filter.experienceLevels = experienceFacet && experienceFacet.results.some(x => x.isSelected)
      ? experienceFacet.results.filter(x => x.isSelected).map(x => x.value) : null;
      request.filter.locationNames = locationNameFacet && locationNameFacet.results.some(x => x.isSelected)
        ? locationNameFacet.results.filter(x => x.isSelected).map(x => x.value) : null;

    if (this.search !== undefined && this.search !== '') {
      request.filter.search = this.search;
    }

    if (!this.isInternal) {
      request.visibilityType = VisibilityTypes.External;
    } else {
      request.visibilityType = VisibilityTypes.Internal;
      request.internalJobBoardIdentifier = this.internalJobBoardIdentifier;
    }

    const url = this.location.path().indexOf('?') === -1 ?
      this.location.path() : this.location.path().substring(0, this.location.path().indexOf('?'));
    this.location.replaceState(url, request.toQueryString());

    return this.service.getJobs(request);
  }
  
  private markIsSelected(facetResult: FacetResult, filter: any) {
    if (!filter) { return; }
    if ((filter instanceof Array && filter.some(x => x === facetResult.value))
      || filter === facetResult.value) { facetResult.isSelected = true; }
  }
}
